import { ZfEventCard, ZfLoadingSpinner } from '@zafire/react';
import type { Moment } from 'moment';
import moment from 'moment/moment';
import type { FunctionComponent } from 'react';
import { Suspense, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { getImageUrlByString } from '../../../styles/assets/imageMap';
import { useClient, useEvents } from '../../api';
import type { MaturityDto, QualifiedIdentity } from '../../api/generated/client';
import { useYwuApp } from '../../contexts/ywu-app-context';
import { useActiveEvent } from '../../hooks/useActiveEvent';
import { useBalanceEntries } from '../../hooks/useBalanceAccounts';
import type { RoadmapEvent } from '../../interfaces/event';
import { isLifeEvent, isMaturity } from '../../utils/eventUtils';
import { formatCashFlowAmount } from '../../utils/renderUtils';
import { NeedEventModal } from '../topic/modals/NeedEventModal';
import { BalanceEntryModalWrapper } from '../wealth';
import type { IEventTimelineItem } from './timeline/EventTimeline';
import { EventTimeline } from './timeline/EventTimeline';

interface IProps {
    dateFrom: Moment;
    dateTo: Moment;
}

export const TimeLineEvents: FunctionComponent<IProps> = ({ dateFrom, dateTo }) => {
    const navigate = useNavigate();
    const { partnerUuid } = useParams();
    const [activeEvent, setActiveEvent] = useActiveEvent();

    const {
        data: { lifeEvents, maturities },
    } = useEvents();
    const balanceEntries = useBalanceEntries();
    const { data: client } = useClient();

    const { activeLanguage } = useYwuApp();

    const getBalanceEntryById = (qualifiedIdentity: QualifiedIdentity) => balanceEntries.find((entry) => entry.qualifiedKey === qualifiedIdentity.qualifiedKey);

    const onGoToTopicClick = (topicId: number) => navigate(`../topics/${topicId}`);

    const onCloseModalClick = () => {
        setActiveEvent(undefined);
    };

    const handleEditEvent = (event: RoadmapEvent) => setActiveEvent(event);

    const handleCloseWebComponent = () => {
        setActiveEvent(undefined);
    };

    const showEditModal = isLifeEvent(activeEvent);
    const showEditClmModal = isMaturity(activeEvent);

    const groupedByRow = useMemo(() => {
        const events: RoadmapEvent[] = [...lifeEvents, ...maturities];
        let maxRow = 1;
        const getLastEvent = (row: number) => groupedByRow[row][groupedByRow[row].length - 1];
        const isOver18Months = (previousDate: string, currentDate: string) => moment(currentDate).diff(previousDate, 'months') > 18;
        const groupedByRow: { [key: number]: RoadmapEvent[] } = {};

        events?.forEach((event) => {
            let isAdded = false;
            for (let row = 0; row < maxRow; row++) {
                if (!groupedByRow[row] || isOver18Months(getLastEvent(row).dueDate ?? '', event.dueDate ?? '')) {
                    groupedByRow[row] = [...(groupedByRow[row] || []), event];
                    isAdded = true;
                    break;
                }
            }
            if (!isAdded) {
                groupedByRow[maxRow] = [event];
                maxRow++;
            }
        });

        return groupedByRow;
    }, [lifeEvents, maturities]);

    const timeLineItems: IEventTimelineItem[] = [];
    Object.keys(groupedByRow).forEach((key) => {
        groupedByRow[Number(key)].forEach((event) => {
            timeLineItems.push({
                // @ts-expect-error Problems with RoadmapEvent type because 3 different interfaces possible
                key: String(Object.prototype.hasOwnProperty.call(event, 'id') ? event.id : event.uuid),
                year: moment(event.dueDate).year(),
                month: moment(event.dueDate).month(),
                row: Number(key) + 1,
                content: (
                    <ZfEventCard
                        titleText={event.name}
                        onClick={() => handleEditEvent(event)}
                        onCategoryClick={(e: CustomEvent) => {
                            e.detail.stopPropagation();
                            onGoToTopicClick(event.topicId!);
                        }}
                        image={getImageUrlByString(event.topicKey)}
                        category={event.topicName?.[activeLanguage]}
                        date={event.dueDate}
                        {...(event.isCashFlowRelevant && { amount: formatCashFlowAmount(event.signedAmount!, event.type!, event.currency!) })}
                    />
                ),
            });
        });
    });

    return (
        <Suspense fallback={<ZfLoadingSpinner />}>
            <EventTimeline
                birthYear={client?.birthdate ? moment(client.birthdate).year() : undefined}
                startYear={dateFrom.get('year')}
                endYear={dateTo.get('year')}
                items={timeLineItems}
            />
            {showEditModal && <NeedEventModal event={activeEvent} onClose={onCloseModalClick} isOpen={showEditModal} />}
            {showEditClmModal && (
                <BalanceEntryModalWrapper
                    woCategoryId={(activeEvent as MaturityDto).categoryId}
                    partnerUid={partnerUuid}
                    balanceEntry={getBalanceEntryById(activeEvent as MaturityDto)}
                    onClose={handleCloseWebComponent}
                />
            )}
        </Suspense>
    );
};
